import {
  useCallback,
  Dispatch,
  SetStateAction,
  MutableRefObject,
  useRef
} from 'react'
import dayjs from 'dayjs'
import { useAppDispatch } from '../../../../app/hooks'
import { FilterState, DepartmentV2 } from '../types'
import { DepartmentMemberV2 } from '../../../../global'
import {
  fetchNlpFilterResult,
  fetchNlpKeywordPolishing
} from '../services/nlpFilterService'
import actionTypes from '../../../../constants/actionTypes'

type UseNlpKeywordSubmitParams = {
  groupID?: string
  /** 是否允许在该场景下触发 NLP 接口 */
  enableNlp?: boolean
  onSubmit: (
    latestKeywords?: string[],
    overrideFilter?: Partial<FilterState>,
    options?: { preserveInput?: boolean }
  ) => void
  onValueChange: (keywords: string[], opts?: { keepInput?: boolean }) => void
  // 当在 preserveInput 场景下把润色后的文本放回输入框，需要传入此回调
  onInputChange?: (text: string) => void
  addKeywords: (keywords: string[], meta: { isNlp: boolean }) => void
  inputValueRef: MutableRefObject<string>
  setDropdownSuppressToken: Dispatch<SetStateAction<number>>
}

type UseNlpKeywordSubmitResult = {
  handleSearch: (
    currentTags?: string[],
    options?: { preserveInput?: boolean }
  ) => Promise<void>
  submitWithNonNlp: (
    latestSearchKeywords?: string[],
    overrideFilter?: Partial<FilterState>
  ) => void
}

export const useNlpKeywordSubmit = ({
  groupID,
  enableNlp = true,
  onSubmit,
  onValueChange,
  onInputChange,
  addKeywords,
  inputValueRef,
  setDropdownSuppressToken
}: UseNlpKeywordSubmitParams): UseNlpKeywordSubmitResult => {
  const dispatch = useAppDispatch()
  // 记录上一次 NLP 接口返回的 isNlpFilter 状态
  const lastNlpFilterRef = useRef<boolean | null>(null)

  const submitWithNonNlp = useCallback(
    (
      latestSearchKeywords?: string[],
      overrideFilter?: Partial<FilterState>,
      options?: { preserveInput?: boolean }
    ) => {
      const mergedOverride = {
        ...(overrideFilter || {}),
        isNlpFilter: false
      }
      // 隐藏下拉框
      setDropdownSuppressToken((token) => token + 1)
      onSubmit(latestSearchKeywords, mergedOverride, options)
    },
    [onSubmit, setDropdownSuppressToken]
  )

  const handleSearch = useCallback(
    async (
      currentTags: string[] = [],
      options?: { preserveInput?: boolean }
    ) => {
      const rawInput = inputValueRef.current.trim()
      const inputKeywords = rawInput
        ? rawInput.split(/\s+/).filter(Boolean)
        : []

      // 合并现有 tags 和输入框内容
      const allKeywords = Array.from(
        new Set([...currentTags, ...inputKeywords])
      )

      if (allKeywords.length === 0) {
        if (!options?.preserveInput) {
          onValueChange([])
        }
        submitWithNonNlp([], undefined, options)
        return
      }

      let overrideFilter: Partial<FilterState> | undefined
      let finalKeywords: string[] = allKeywords

      // 仅当只有一个关键词（一个 tag 或一句话输入）时，且场景允许时，才尝试 NLP 搜索
      if (enableNlp && groupID && allKeywords.length === 1) {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: true
        })
        try {
          // NLP 搜索使用的字符串：如果有输入内容，优先用输入内容（可能是一句话）；
          // 如果输入为空，则把现有 tags 拼起来搜。
          const keywordForNlp = rawInput || allKeywords.join(' ')

          const res = await fetchNlpFilterResult({
            groupID,
            keyword: keywordForNlp
          })
          const data = res?.data
          const isSuccessCode = res?.code === 200
          const isSuccessStatus = data?.status === 0
          const currentIsNlpFilter = data?.isNlpFilter === true

          // 如果当前接口返回 isNlpFilter 为 false，但上一次返回的是 true，则清除筛选条件
          if (
            isSuccessCode &&
            isSuccessStatus &&
            data &&
            !currentIsNlpFilter &&
            lastNlpFilterRef.current === true
          ) {
            const nlpDepartmentList: string[] =
              data.filter?.departmentInfoList?.map((item: any) => item.name) ||
              []

            // 清除 NLP 筛选条件（部门、用户、时间等）
            overrideFilter = {
              departmentUsers: [],
              departments: [],
              times: [
                dayjs().subtract(6, 'months').startOf('day'),
                dayjs().endOf('day')
              ],
              isNlpFilter: false,
              nlpResponseJson: JSON.stringify(res),
              // NLP 相关字段仅用于埋点，不影响筛选含义
              ...(nlpDepartmentList.length ? { nlpDepartmentList } : {}),
              // 记录用户原始输入文本，供埋点 searchText 使用
              keyword: rawInput
            }
            // 更新记录
            lastNlpFilterRef.current = false
            if (!options?.preserveInput) {
              onValueChange(finalKeywords)
            }
            if (allKeywords.length > 0) {
              addKeywords(allKeywords, { isNlp: false })
            }
            onSubmit(finalKeywords, overrideFilter, options)
            return
          }

          if (isSuccessCode && isSuccessStatus && data && currentIsNlpFilter) {
            const filterData = data.filter || {}
            const departmentUsers: DepartmentMemberV2[] =
              filterData.userInfoList?.map((item) => ({
                id: item.id,
                userID: item.userID || item.id,
                name: item.name,
                avatar: item.avatar || '',
                quit: false,
                role: 0
              })) || []

            const nlpDepartmentList: string[] =
              filterData.departmentInfoList?.map((item: any) => item.name) || []

            const departments: DepartmentV2[] =
              filterData.departmentInfoList?.map((item) => ({
                id: String(item.id),
                name: item.name,
                num: item.peopleNum,
                avatar: item.avatar
              })) || []

            let searchKeywords: string[]
            if (filterData.keywords && filterData.keywords.length > 0) {
              searchKeywords = filterData.keywords
            } else if (
              filterData.keywords &&
              filterData.keywords.length === 0
            ) {
              searchKeywords = []
            } else {
              searchKeywords = allKeywords
            }

            let times: FilterState['times'] | undefined
            if (
              filterData.timeParams?.startTime &&
              filterData.timeParams?.endTime
            ) {
              const start = dayjs(filterData.timeParams.startTime)
              const end = dayjs(filterData.timeParams.endTime)
              if (start.isValid() && end.isValid()) {
                times = [start, end]
              }
            }

            // 将 NLP 响应的完整 JSON 保存为字符串
            const nlpResponseJson = JSON.stringify(res)

            // 如果 NLP 返回的“部门为空且成员为空”，说明没有命中可用于筛选的成员/部门：
            // 此时不覆盖用户当前筛选条件（包括时间）。
            const hasAnyMemberOrDept =
              departmentUsers.length > 0 || departments.length > 0

            const overrideFilterBase: Partial<FilterState> = {
              keyword: keywordForNlp,
              searchKeywords,
              isNlpFilter: true,
              nlpResponseJson,
              ...(nlpDepartmentList.length ? { nlpDepartmentList } : {})
            }

            if (hasAnyMemberOrDept) {
              // 沿用原先覆盖逻辑：只要任意一侧有数据，就把筛选条件覆盖成 NLP 返回值
              overrideFilterBase.departmentUsers = departmentUsers
              overrideFilterBase.departments = departments

              if (times) {
                overrideFilterBase.times = times
              }
            }

            overrideFilter = overrideFilterBase

            finalKeywords = searchKeywords

            // 更新记录：本次 NLP 接口返回 true
            lastNlpFilterRef.current = true
            setDropdownSuppressToken((token) => token + 1)

            // NLP 命中后，异步调用独立的关键词润色接口，不阻塞主流程
            fetchNlpKeywordPolishing({
              keyword: keywordForNlp,
              groupID,
              userInfoList: filterData.userInfoList,
              departmentInfoList: filterData.departmentInfoList
            })
              .then((polishRes) => {
                const polishedText =
                  typeof polishRes?.data?.keywordPolished === 'string'
                    ? polishRes.data.keywordPolished
                    : undefined

                if (!polishedText) return

                // 如果 preserveInput是true，把润色后的文本放回输入框
                if (options?.preserveInput) {
                  inputValueRef.current = polishedText
                  // 回调到外层以同步显示
                  if (typeof (onInputChange as any) === 'function') {
                    ;(onInputChange as any)(polishedText)
                  }
                } else {
                  // NLP：润色结果展示在输入框，不写入 tag
                  inputValueRef.current = polishedText
                  if (typeof (onInputChange as any) === 'function') {
                    ;(onInputChange as any)(polishedText)
                  }
                  onValueChange([], { keepInput: true })
                }
              })
              .catch((err) => {
                console.error('NLP 关键词润色接口调用失败:', err)
              })

            if (allKeywords.length > 0) {
              addKeywords(allKeywords, { isNlp: true })
            }
            onSubmit(searchKeywords, overrideFilter, options)
            return
          }

          // 如果接口调用成功但 isNlpFilter 为 false，更新记录
          if (isSuccessCode && isSuccessStatus && data) {
            lastNlpFilterRef.current = false
          }
        } catch (error) {
          console.error('NLP 接口调用失败:', error)
        }
      }

      if (!options?.preserveInput) {
        onValueChange(finalKeywords)
      }
      if (allKeywords.length > 0) {
        addKeywords(allKeywords, { isNlp: false })
      }
      submitWithNonNlp(finalKeywords, undefined, options)
    },
    [
      groupID,
      inputValueRef,
      onValueChange,
      submitWithNonNlp,
      setDropdownSuppressToken,
      addKeywords,
      onInputChange,
      onSubmit,
      dispatch
    ]
  )

  return {
    handleSearch,
    submitWithNonNlp
  }
}
